import { useEffect } from 'react';
import { Button, Card, FormControl, FormGroup, FormLabel } from 'react-bootstrap';
import { useController, useForm } from 'react-hook-form';
import Select from 'react-select';
import DateTime from 'react-datetime';
import moment from 'moment';
import { ValidationErrors, toRule } from 'forms/rhf';
import { validators } from '../../../utils/validators';
import { initialStates } from '../../../initialStates';

import '../../common/form/elements/searchable-select.css';

const toOption = outlet => ({
  value: outlet.id,
  label: `${outlet.attributes['customer-number']} - ${outlet.attributes.name}`,
});

// Delink wizard step 1. Migrated from react-redux-form to react-hook-form:
// the outlet <Select> and the date picker are driven locally via useController
// (rather than the shared rrf-bound filtered-dropdown/datepicker, which other
// forms still use). Submit payload contract is unchanged:
// { outlet, delink_date, delink_reason, supress_notification }.
export default function DelinkForm({
  outlets, fetched, selectedOutlet, handleSubmit: onFinish, onInputChange,
  values = initialStates.admin_delink,
}) {
  // `values` carries the wizard's current entries so returning from the
  // confirmation step (Back) re-renders with what the user typed, not a reset.
  const {
    control, register, handleSubmit, setValue, formState: { errors },
  } = useForm({
    defaultValues: {
      ...values,
      outlet: values.outlet || (selectedOutlet ? selectedOutlet.id : ''),
    },
  });

  // The outlet fetched from the URL id can arrive after first render; preselect it.
  useEffect(() => {
    if (selectedOutlet && selectedOutlet.id) {
      setValue('outlet', selectedOutlet.id, { shouldDirty: false });
    }
  }, [selectedOutlet, setValue]);

  const { field: outletField } = useController({ name: 'outlet', control });
  const { field: dateField } = useController({ name: 'delink_date', control });

  const options = outlets.map(toOption);
  let selectedValue = null;
  if (outletField.value) {
    selectedValue = options.find(option => option.value === outletField.value)
      || (selectedOutlet && selectedOutlet.id === outletField.value
        ? { value: selectedOutlet.id, label: selectedOutlet.attributes.name }
        : null);
  }

  return (
    <form onSubmit={handleSubmit(payLoad => onFinish(payLoad, outlets))}>
      <ValidationErrors className="errors" errors={errors}/>
      <Card className="mg-top-20">
        <Card.Body>
          <FormGroup controlId="outlet">
            <label>
              Delink Single Outlet
            </label>
            <Select
              classNamePrefix="rs"
              isClearable
              isLoading={!fetched}
              onChange={option => outletField.onChange(option ? option.value : '')}
              onInputChange={(input) => {
                if (onInputChange) onInputChange(input);
                return input;
              }}
              options={options}
              placeholder="Select Outlet"
              value={selectedValue}
            />
          </FormGroup>
          <FormGroup controlId="delink_date">
            <FormLabel>
              Delink date*
            </FormLabel>
            <DateTime
              closeOnSelect
              dateFormat="DD/MM/YYYY"
              inputProps={{
                // Associates the FormGroup controlId label with this input.
                id: 'delink_date',
                required: true,
                // Thanks to Chrome: https://bugs.chromium.org/p/chromium/issues/detail?id=587466
                autoComplete: `${+new Date()}-${Math.random()}`,
              }}
              onChange={value => dateField.onChange(typeof value === 'object' ? value.format() : '')}
              timeFormat={false}
              value={dateField.value ? moment(dateField.value) : moment()}
            />
          </FormGroup>
          <FormGroup controlId="delink_reason">
            <FormLabel>
              Delink reason*
            </FormLabel>
            <FormControl
              {...register('delink_reason', {
                validate: { required: toRule(validators.required(), 'Delink reason is required') },
              })}
            />
          </FormGroup>
          <FormGroup controlId="supress_notification">
            <label>
              <input type="checkbox" {...register('supress_notification')}/>
              Suppress notifications
            </label>
          </FormGroup>
        </Card.Body>
        <Card.Footer>
          <div className="fluid text-end">
            <Button className="btn red-ghost-btn" type="submit">
              Next
            </Button>
          </div>
        </Card.Footer>
      </Card>
    </form>
  );
}
